// Phase 36 Batch T — Admin Analytics > Usage tab.
//
// DAU / WAU / MAU line chart + deals-per-day bar + top event types
// pie. Investor-grade premium chrome (white bg / slate text / indigo
// accent) — no playful styling.

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Loader2, Activity, AlertTriangle } from "lucide-react";

const INDIGO = "#4f46e5";
const SLATE = "#475569";
const EMERALD = "#10b981";
const PIE_COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

export default function AdminAnalyticsUsage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/admin/analytics/usage?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  // Merge DAU/WAU/MAU into a single dataset so we can draw three lines.
  const usageSeries = useMemo(() => {
    if (!data) return [];
    return data.dau_series.map((d, i) => ({
      date: d.date.slice(5), // MM-DD for axis brevity
      DAU: d.count,
      WAU: data.wau_series[i]?.count ?? 0,
      MAU: data.mau_series[i]?.count ?? 0,
    }));
  }, [data]);

  const dealsSeries = useMemo(() => {
    if (!data) return [];
    return data.deals_per_day.map((d) => ({ date: d.date.slice(5), deals: d.count }));
  }, [data]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return (data.top_event_types || []).slice(0, 8).map((t) => ({
      name: t.event_type, value: t.count,
    }));
  }, [data]);

  return (
    <div className="space-y-6" data-testid="admin-analytics-usage">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-indigo-600">Analytics · Usage</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
            Active users &amp; engagement
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Daily / weekly / monthly active users — and what they did.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Window</span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32" data-testid="usage-window-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {err && (
        <Card className="flex items-center gap-3 border-rose-200 bg-rose-50/60 p-4 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4" /> {err}
        </Card>
      )}

      {loading || !data ? (
        <Card className="flex h-72 items-center justify-center text-slate-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading usage…
        </Card>
      ) : (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { label: "DAU (today)", value: usageSeries.at(-1)?.DAU ?? 0, color: "text-indigo-700" },
              { label: "WAU (trailing 7d)", value: usageSeries.at(-1)?.WAU ?? 0, color: "text-emerald-700" },
              { label: "MAU (trailing 30d)", value: usageSeries.at(-1)?.MAU ?? 0, color: "text-amber-700" },
            ].map((kpi) => (
              <Card key={kpi.label} className="p-5">
                <p className="text-xs uppercase tracking-wider text-slate-500">{kpi.label}</p>
                <p className={`mt-2 text-3xl font-semibold ${kpi.color}`}>{kpi.value.toLocaleString()}</p>
              </Card>
            ))}
          </div>

          {/* DAU/WAU/MAU line chart */}
          <Card className="p-5">
            <div className="mb-2 flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-slate-900">Active users</h2>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={usageSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke={SLATE} fontSize={11} />
                  <YAxis stroke={SLATE} fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="DAU" stroke={INDIGO} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="WAU" stroke={EMERALD} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="MAU" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Deals + event types side-by-side */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card className="p-5">
              <h2 className="mb-2 text-sm font-semibold text-slate-900">Deals created per day</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dealsSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke={SLATE} fontSize={11} />
                    <YAxis stroke={SLATE} fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="deals" fill={INDIGO} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="mb-2 text-sm font-semibold text-slate-900">Top event types</h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(d) => d.name} labelLine={false} fontSize={11}>
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>

          {/* Raw event-type table for the long tail. */}
          <Card className="overflow-hidden">
            <div className="border-b border-slate-200 px-5 py-3">
              <h2 className="text-sm font-semibold text-slate-900">All event types ({data.total_events.toLocaleString()})</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-2 text-left">Event type</th>
                  <th className="px-5 py-2 text-right">Count</th>
                  <th className="px-5 py-2 text-right">% of total</th>
                </tr>
              </thead>
              <tbody>
                {(data.top_event_types || []).map((t) => (
                  <tr key={t.event_type} className="border-t border-slate-100">
                    <td className="px-5 py-2 font-mono text-xs text-slate-700">{t.event_type}</td>
                    <td className="px-5 py-2 text-right tabular-nums text-slate-900">{t.count.toLocaleString()}</td>
                    <td className="px-5 py-2 text-right tabular-nums text-slate-500">
                      {data.total_events > 0 ? ((100 * t.count) / data.total_events).toFixed(1) : "0.0"}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
